import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { IMAGES } from "@/config";
import { SESSION } from "@/config/session";
import { ImageSlot, Logo } from "./Primitives";
import { DetailsCard, RegisterCard } from "./HeroCards";

const EASE = [0.22, 1, 0.36, 1];
const H1 = [
  { t: "Why Ex-Banker" },
  { t: "Sandeep Gupta", hl: true },
  { t: "Is Revealing" },
  { t: "'The Wealth Code'", hl: true },
  { t: "He Has Taught 16,000+ Indians —" },
  { t: "Live & 100% FREE?" },
];

const MaskedHeadline = () => (
  <h1 data-testid="hero-headline" className="font-extrabold text-ink text-center text-[25px] sm:text-[34px] lg:text-[42px] leading-[1.22] tracking-tight max-w-4xl mx-auto">
    {H1.flatMap((chunk, ci) =>
      chunk.t.split(" ").map((w, wi) => (
        <span key={`${ci}-${wi}`} className="inline-block overflow-hidden align-bottom pb-[0.08em] mr-[0.25em]">
          <motion.span
            className={`inline-block ${chunk.hl ? "text-plum" : ""}`}
            initial={{ y: "105%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.15 + (ci * 3 + wi) * 0.04 }}
          >
            {w}
          </motion.span>
        </span>
      ))
    )}
  </h1>
);

const HeroPhoto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [30, -30]);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 120, damping: 14 });
  const sry = useSpring(ry, { stiffness: 120, damping: 14 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 10);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 10);
  };
  return (
    <div ref={ref} className="relative [perspective:1000px]" onMouseMove={onMove} onMouseLeave={() => { rx.set(0); ry.set(0); }}>
      <div className="absolute inset-0 -z-10 spotlight" aria-hidden />
      <motion.div style={{ y, rotateX: srx, rotateY: sry }} className="relative mx-auto w-[88%] sm:w-full max-w-[460px] aspect-[4/5]">
        <div className="absolute -inset-0 translate-x-3 translate-y-3 rounded-[28px] border-2 border-tang" aria-hidden />
        <div className="relative w-full h-full rounded-[28px] overflow-hidden bg-white shadow-[0_30px_60px_-20px_rgba(91,33,214,0.45)]">
          <ImageSlot src={IMAGES.hero} label="Hero photo of Sandeep Gupta (portrait, 4:5)" alt="Sandeep Gupta, Financial Freedom Coach" position="center top" testId="hero-image-slot" />
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.1, duration: 0.6, ease: EASE }}
          className="absolute -left-2 sm:-left-5 bottom-8 bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3"
        >
          <Logo className="w-9 h-9" />
          <div className="leading-tight">
            <div className="font-black text-ink text-lg">16,000+</div>
            <div className="text-[12px] text-zinc-600 font-medium">Indians taught</div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const Hero = () => (
  <section className="hero-bg relative pt-5 pb-10 sm:pt-8 sm:pb-16 px-4" data-testid="hero-section">
    <div className="max-w-6xl mx-auto">
      <motion.p
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        data-testid="urgency-line"
        className="mx-auto w-fit max-w-full text-center rounded-full bg-plum/10 text-plum px-4 py-1.5 text-[11.5px] sm:text-[13px] font-semibold mb-4 sm:mb-6"
      >
        <span className="font-extrabold">URGENT:</span> If your money isn't growing faster than your expenses, this {SESSION.dayLabel} is for you.
      </motion.p>
      <MaskedHeadline />
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9, duration: 0.8 }}
        data-testid="hero-subheadline"
        className="text-center text-zinc-700 text-[14.5px] sm:text-[17px] max-w-2xl mx-auto mt-4 leading-relaxed"
      >
        Discover why your money isn't growing even when you earn and save well — and the{" "}
        <span className="font-bold text-ink underline decoration-tang decoration-2 underline-offset-4">simple code wealthy families follow</span> to build lasting wealth.
      </motion.p>
      <div className="mt-8 sm:mt-12 grid lg:grid-cols-[1.05fr_1fr] gap-8 lg:gap-14 items-center">
        <HeroPhoto />
        <div className="space-y-4">
          <DetailsCard />
          <RegisterCard />
        </div>
      </div>
    </div>
  </section>
);
